/**
 * 네이버 지도 인스턴스 생성/관리 훅
 */

import { useEffect, useRef, type RefObject } from 'react'
import { useNaverMapSDK } from './useNaverMapSDK.js'

interface UseNaverMapOptions {
  lat: number
  lng: number
  zoom?: number
  interactive?: boolean
  label?: string
}

function buildInfoContent(label: string) {
  return [
    '<div style="',
    'padding:6px 14px;',
    'font-size:13px;',
    'font-weight:600;',
    'white-space:nowrap;',
    'color:#1a1a1a;',
    'background:#fff;',
    'border-radius:20px;',
    'box-shadow:0 2px 12px rgba(0,0,0,.15);',
    'letter-spacing:0.01em;',
    '">',
    label,
    '</div>',
  ].join('')
}

export function useNaverMap(
  containerRef: RefObject<HTMLDivElement | null>,
  options: UseNaverMapOptions,
) {
  const { isLoaded, error } = useNaverMapSDK()
  const mapRef = useRef<naver.maps.Map | null>(null)
  const markerRef = useRef<naver.maps.Marker | null>(null)
  const infoRef = useRef<naver.maps.InfoWindow | null>(null)

  const { lat, lng, zoom = 16, interactive = true, label } = options

  // 지도 생성
  useEffect(() => {
    if (!isLoaded || !containerRef.current) return

    const center = new naver.maps.LatLng(lat, lng)

    const map = new naver.maps.Map(containerRef.current, {
      center,
      zoom,
      draggable: interactive,
      scrollWheel: interactive,
      pinchZoom: interactive,
      disableDoubleClickZoom: !interactive,
      keyboardShortcuts: interactive,
      zoomControl: false,
      mapDataControl: false,
      scaleControl: false,
    })

    const marker = new naver.maps.Marker({ map, position: center })

    mapRef.current = map
    markerRef.current = marker

    if (label) {
      const infoWindow = new naver.maps.InfoWindow({
        content: buildInfoContent(label),
        borderWidth: 0,
        backgroundColor: 'transparent',
        anchorSize: new naver.maps.Size(0, 0),
        pixelOffset: new naver.maps.Point(0, -8),
      })
      infoWindow.open(map, marker)
      infoRef.current = infoWindow
    }

    return () => {
      try { infoRef.current?.close() } catch { /* 네이버 SDK 내부 에러 무시 */ }
      try { marker.setMap(null) } catch { /* noop */ }
      try { map.destroy() } catch { /* noop */ }
      mapRef.current = null
      markerRef.current = null
      infoRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, label])

  // lat/lng 변경 시 업데이트
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return
    const pos = new naver.maps.LatLng(lat, lng)
    mapRef.current.setCenter(pos)
    markerRef.current.setPosition(pos)

    // InfoWindow도 마커 따라 이동
    if (infoRef.current && markerRef.current) {
      infoRef.current.open(mapRef.current, markerRef.current)
    }
  }, [lat, lng])

  return { isLoaded, error }
}
